import { useEffect, useState } from "react";
import { BookOpen, CheckCircle2, Play, Trophy, Award, Star, ShieldCheck, Lock, Lightbulb, Download, ExternalLink, ClipboardList } from "lucide-react";
import { api } from "../lib/api";
import { Modal } from "../components/Modal";
import { DBS_TYPES } from "../lib/constants";
import { fetchVimeoThumbnail } from "../lib/vimeo";

const ROLEPLAY_VIDEO = "https://player.vimeo.com/video/861847959";

// Resource catalogue. Cover images are looked up from /public/resources/<cover>.jpg
// (or .png) — if missing we fall back to a brand-styled gradient with the title overlay.
type Resource = {
  id: string;
  title: string;
  subtitle?: string;
  cover?: string;          // basename without extension; matches /public/resources/<cover>.{jpg,png}
  href: string;
  badge?: "campaign" | "guide" | "tool";
};

const FEATURED_RESOURCES: Resource[] = [
  {
    id: "go-make",
    title: "Go Make Disciples Campaign Guide",
    subtitle: "Equipping every believer to go make disciples that multiply.",
    cover: "go-make-disciples",
    href: "https://assets.cdn.filesafe.space/CnkvHlZT1PbWAsOvJlcL/media/699e6fa85dfd5035eb01f04b.pdf",
    badge: "campaign",
  },
  {
    id: "discipleship-lite",
    title: "Discipleship Series (Lite version)",
    cover: "discipleship-lite",
    href: "https://storage.googleapis.com/msgsndr/CnkvHlZT1PbWAsOvJlcL/media/6477f354b39fa8cdf02a5962.pdf",
    badge: "guide",
  },
];

const QUICK_RESOURCES: Resource[] = [
  {
    id: "how-to-run",
    title: "How to Run a Discovery Bible Study",
    cover: "how-to-run-dbs",
    href: "https://storage.googleapis.com/msgsndr/CnkvHlZT1PbWAsOvJlcL/media/64126baf6230008921cf6995.pdf",
  },
  {
    id: "disciple-maker",
    title: "What is a Disciple maker?",
    cover: "what-is-a-disciple-maker",
    href: "https://storage.googleapis.com/msgsndr/CnkvHlZT1PbWAsOvJlcL/media/6413b299623000a3b2d0402c.pdf",
  },
  {
    id: "prayer-strategy",
    title: "Prayer Strategy",
    cover: "prayer-strategy",
    href: "https://storage.googleapis.com/msgsndr/CnkvHlZT1PbWAsOvJlcL/media/64d59be648429601ee3b7405.pdf",
  },
];

const FOUR_ELEVEN_URL = "https://thrivedigitalau.typeform.com/to/W3kvGJJk";

interface Module {
  id: string; title: string; module_number: number; description: string;
  video_url: string; cdm_only: number; required: number; sort_order: number;
}
interface Leader {
  id: string; name: string; is_cdm: number;
  approved_at: number | null; training_confirmed_at: number | null;
}
interface Progress { module_id: string; completed_at: number | null }

export default function Training() {
  const [modules, setModules] = useState<Module[]>([]);
  const [leaders, setLeaders] = useState<Leader[]>([]);
  const [leaderId, setLeaderId] = useState<string>("");
  const [progress, setProgress] = useState<Progress[]>([]);
  const [viewing, setViewing] = useState<Module | null>(null);
  const [reviewedAll, setReviewedAll] = useState(false);
  const [confirmErr, setConfirmErr] = useState<string | null>(null);

  const leader = leaders.find((l) => l.id === leaderId);

  useEffect(() => {
    api.get<{ leaders: Leader[] }>("/leaders").then((l) => {
      setLeaders(l.leaders);
      if (l.leaders[0]) setLeaderId(l.leaders[0].id);
    });
  }, []);

  // Re-fetch modules whenever leader changes (CDM modules toggle).
  useEffect(() => {
    const path = leaderId ? `/training/modules?leader_id=${leaderId}` : "/training/modules";
    api.get<{ modules: Module[] }>(path).then((m) => setModules(m.modules));
  }, [leaderId]);

  useEffect(() => {
    if (!leaderId) { setProgress([]); return; }
    api.get<{ progress: Progress[] }>(`/training/progress/${leaderId}`).then((r) => setProgress(r.progress));
    setReviewedAll(false);
    setConfirmErr(null);
  }, [leaderId]);

  const completedSet = new Set(progress.filter((p) => p.completed_at).map((p) => p.module_id));
  const requiredModules = modules.filter((m) => m.required === 1);
  const optionalModules = modules.filter((m) => m.required === 0);
  const completedRequired = requiredModules.filter((m) => completedSet.has(m.id)).length;
  const requiredPct = requiredModules.length ? Math.round((completedRequired / requiredModules.length) * 100) : 0;
  const allRequiredDone = requiredModules.length > 0 && completedRequired === requiredModules.length;

  const toggle = async (m: Module) => {
    if (!leaderId) return;
    if (completedSet.has(m.id)) {
      await api.post(`/training/progress/${leaderId}/${m.id}/uncomplete`);
    } else {
      await api.post(`/training/progress/${leaderId}/${m.id}/complete`);
    }
    const r = await api.get<{ progress: Progress[] }>(`/training/progress/${leaderId}`);
    setProgress(r.progress);
  };

  const confirmTraining = async () => {
    setConfirmErr(null);
    try {
      await api.post(`/leaders/${leaderId}/confirm-training`);
      // Refresh leader so we get the new training_confirmed_at
      const l = await api.get<{ leaders: Leader[] }>("/leaders");
      setLeaders(l.leaders);
      setReviewedAll(false);
    } catch (e: any) {
      setConfirmErr(e.message);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Training Hub</h1>
        <p className="text-ink-500 text-sm">Watch training videos, complete required modules, then confirm to activate</p>
      </div>

      <div className="card p-5 flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label className="label">Track progress for leader</label>
          <select className="input" value={leaderId} onChange={(e) => setLeaderId(e.target.value)}>
            {leaders.length === 0 && <option value="">No leaders yet — add one in Leaders</option>}
            {leaders.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}{l.is_cdm ? " · CDM" : ""}
              </option>
            ))}
          </select>
        </div>
        {leader && (
          <div className="grid grid-cols-3 gap-3 flex-1 min-w-[260px]">
            <Mini label="Required" value={requiredModules.length} accent="text-blue-700" icon={<BookOpen className="h-4 w-4" />} />
            <Mini label="Completed" value={completedRequired} accent="text-green-700" icon={<CheckCircle2 className="h-4 w-4" />} />
            <Mini label="Progress" value={`${requiredPct}%`} accent="text-brand" icon={<Trophy className="h-4 w-4" />} />
          </div>
        )}
      </div>

      {leader && !leader.approved_at && (
        <div className="card p-4 bg-amber-50 border border-amber-200 text-sm flex items-start gap-3">
          <Lock className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-amber-900">
            <div className="font-semibold">Pending approval</div>
            This leader can work through training now, but an admin must approve them before they can be confirmed as <strong>Active</strong>.
          </div>
        </div>
      )}

      {leader && !leader.training_confirmed_at && (
        <div className="card p-5">
          <div className="flex justify-between text-sm">
            <span className="font-medium">Required Training Progress</span><span className="text-brand font-medium">{requiredPct}%</span>
          </div>
          <div className="h-2 bg-ink-100 rounded-full overflow-hidden mt-2">
            <div className="h-full bg-brand transition-all" style={{ width: `${requiredPct}%` }} />
          </div>
          <div className="text-xs text-ink-500 mt-2">{completedRequired} of {requiredModules.length} required modules completed</div>

          <div className="mt-5 pt-4 border-t border-ink-100">
            <label className={`flex items-start gap-2 text-sm ${allRequiredDone ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}`}>
              <input type="checkbox" disabled={!allRequiredDone} className="mt-1" checked={reviewedAll}
                onChange={(e) => setReviewedAll(e.target.checked)} />
              <span>
                <span className="font-medium">I have reviewed all training</span>
                <span className="block text-xs text-ink-500">Tick once you've watched every required module and you're ready to lead.</span>
              </span>
            </label>
            <button
              onClick={confirmTraining}
              disabled={!reviewedAll || !allRequiredDone || !leader.approved_at}
              className="btn-primary mt-3"
              title={!leader.approved_at ? "Leader must be approved by an admin before activation" : undefined}
            >
              <ShieldCheck className="h-4 w-4" /> Confirm & activate leader
            </button>
            {!leader.approved_at && (
              <div className="text-xs text-ink-500 mt-2">
                Activation is blocked until an admin approves this leader. Once approved, this button enables.
              </div>
            )}
            {confirmErr && <div className="text-sm text-brand mt-2">{confirmErr}</div>}
          </div>
        </div>
      )}

      {leader?.training_confirmed_at && (
        <div className="card p-5 bg-green-50 border border-green-200 text-green-900 flex items-center gap-3">
          <Award className="h-6 w-6" />
          <div className="flex-1">
            <div className="font-semibold">Training confirmed — leader is Active</div>
            <div className="text-sm">All required training is complete and reviewed.</div>
          </div>
        </div>
      )}

      <section>
        <h2 className="font-semibold mb-3 flex items-center gap-2">
          Required Training <span className="text-xs font-normal text-ink-500">({requiredModules.length} modules)</span>
        </h2>
        <ModuleGrid modules={requiredModules} completedSet={completedSet} onView={setViewing} onToggle={toggle} canToggle={!!leader} />
      </section>

      {optionalModules.length > 0 && (
        <section>
          <h2 className="font-semibold mb-1 flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-amber-500" /> Additional Training & DBS Track Resources
            <span className="text-xs font-normal text-ink-500">({optionalModules.length} modules — optional)</span>
          </h2>
          <p className="text-xs text-ink-500 mb-3">Yellow / Green / Blue Light track guides and supplementary materials. Optional but recommended.</p>
          <ModuleGrid modules={optionalModules} completedSet={completedSet} onView={setViewing} onToggle={toggle} canToggle={!!leader} />
        </section>
      )}

      <section className="card p-6">
        <h3 className="font-semibold text-center">Discovery Bible Study Role Play</h3>
        <p className="text-center text-sm text-ink-500 mt-1">Watch this role-play demonstration to see effective DBS facilitation in action</p>
        <div className="aspect-video mt-4 max-w-2xl mx-auto rounded-md overflow-hidden bg-ink-900">
          <iframe
            className="w-full h-full"
            src={ROLEPLAY_VIDEO}
            title="DBS Role Play"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      </section>

      <section>
        <h2 className="font-semibold mb-3">Featured Guides</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {FEATURED_RESOURCES.map((r) => <ResourceCard key={r.id} resource={r} large />)}
        </div>
      </section>

      <section className="card p-6 bg-ink-900 text-white">
        <div className="flex items-start gap-3">
          <ClipboardList className="h-6 w-6 text-brand shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-xl">Complete the 411 Tool (A4 Exercise)</h3>
            <p className="text-sm text-white/80 mt-1">A simple exercise on an A4 sheet answering four core questions.</p>
            <ul className="mt-3 text-sm text-white/90 space-y-1.5">
              <li className="flex gap-2"><span className="text-brand">•</span> Personally complete the 411 tool using an A4 sheet.</li>
              <li className="flex gap-2"><span className="text-brand">•</span> Upload your completed version using the link below.</li>
              <li className="flex gap-2"><span className="text-brand">•</span> Practise articulating it clearly.</li>
              <li className="flex gap-2"><span className="text-brand">•</span> Apply it directly with the person you are discipling 3-4 weeks into the DBS to set up multiplication.</li>
            </ul>
            <a
              href={FOUR_ELEVEN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary mt-5"
            >
              Complete and upload 411 <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </section>

      <section>
        <h2 className="font-semibold mb-3">Quick Resources</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {QUICK_RESOURCES.map((r) => <ResourceCard key={r.id} resource={r} />)}
        </div>
      </section>

      <section className="card p-6">
        <h3 className="font-semibold">DBS Track Reference</h3>
        <p className="text-sm text-ink-500">Each "light" is a different DBS approach — use the right track for where someone is at on their journey.</p>
        <div className="grid sm:grid-cols-3 gap-3 mt-4">
          {DBS_TYPES.map((d) => (
            <div key={d.id} className={`rounded-md p-4 text-sm ${d.chip}`}>
              <div className="flex items-center gap-2 font-semibold">
                <span className={`h-2 w-2 rounded-full ${d.dot}`} />{d.short}
              </div>
              <div className="text-xs opacity-80 mt-1">{d.label}</div>
              <div className="text-xs opacity-70 mt-2">{d.blurb}</div>
            </div>
          ))}
        </div>
      </section>

      <Modal open={!!viewing} onClose={() => setViewing(null)} wide title={viewing ? `Module ${viewing.module_number}: ${viewing.title}` : ""}
        footer={<>
          {viewing && leader && (
            <button className={completedSet.has(viewing.id) ? "btn-secondary" : "btn-primary"} onClick={() => { toggle(viewing); }}>
              {completedSet.has(viewing.id) ? "Mark incomplete" : "Mark complete"}
            </button>
          )}
          <button className="btn-secondary" onClick={() => setViewing(null)}>Close</button>
        </>}>
        {viewing && (
          <div className="space-y-3">
            <div className="aspect-video bg-ink-900 rounded-md overflow-hidden">
              <iframe
                className="w-full h-full"
                src={viewing.video_url}
                title={viewing.title}
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
            <p className="text-sm text-ink-700">{viewing.description}</p>
            <div className="flex gap-2 flex-wrap">
              {viewing.cdm_only === 1 && <span className="badge bg-brand-50 text-brand-700"><Star className="h-3 w-3 mr-1" /> CDM module</span>}
              {viewing.required === 1 ? <span className="badge bg-blue-100 text-blue-800">Required</span> : <span className="badge bg-ink-100 text-ink-700">Optional</span>}
            </div>
            {!leader && <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">Pick a leader at the top of the page to track completion.</p>}
            {leader && !leader.approved_at && <p className="text-xs text-ink-500 bg-ink-50 rounded p-2">Progress is being tracked. Activation needs admin approval first.</p>}
          </div>
        )}
      </Modal>
    </div>
  );
}

function ModuleGrid({ modules, completedSet, onView, onToggle, canToggle }: {
  modules: Module[]; completedSet: Set<string>; onView: (m: Module) => void;
  onToggle: (m: Module) => void; canToggle: boolean;
}) {
  if (modules.length === 0) return <div className="text-sm text-ink-400">None.</div>;
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {modules.map((m) => (
        <ModuleCard
          key={m.id}
          module={m}
          done={completedSet.has(m.id)}
          onView={onView}
          onToggle={onToggle}
          canToggle={canToggle}
        />
      ))}
    </div>
  );
}

function ModuleCard({
  module: m, done, onView, onToggle, canToggle,
}: {
  module: Module; done: boolean;
  onView: (m: Module) => void; onToggle: (m: Module) => void; canToggle: boolean;
}) {
  const [thumb, setThumb] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    fetchVimeoThumbnail(m.video_url).then((u) => { if (alive) setThumb(u); });
    return () => { alive = false; };
  }, [m.video_url]);

  return (
    <div className="card overflow-hidden hover:shadow-md transition group">
      <button onClick={() => onView(m)} className="block w-full text-left">
        <div className="aspect-video relative grid place-items-center bg-gradient-to-br from-ink-200 to-ink-300 overflow-hidden">
          {thumb && (
            <img
              src={thumb}
              alt=""
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
              onError={() => setThumb(null)}
            />
          )}
          <div className="absolute inset-0 bg-black/15 group-hover:bg-black/25 transition" />
          {done && (
            <span className="absolute top-2 right-2 bg-green-600 text-white text-xs rounded-full p-1.5 z-10">
              <CheckCircle2 className="h-3.5 w-3.5" />
            </span>
          )}
          <div className="relative bg-white/90 rounded-full p-3 group-hover:scale-110 transition shadow-md">
            <Play className="h-6 w-6 text-brand fill-brand" />
          </div>
        </div>
      </button>
      <div className="p-4">
        <div className="text-xs text-ink-500 uppercase tracking-wide flex items-center gap-2">
          Module {m.module_number}
          {m.cdm_only === 1 && <span className="badge bg-brand-50 text-brand-700 px-1.5"><Star className="h-2.5 w-2.5 mr-0.5" />CDM</span>}
        </div>
        <div className="font-medium mt-0.5">{m.title}</div>
        <label className={`flex items-center gap-2 text-xs mt-3 ${canToggle ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}`}>
          <input type="checkbox" disabled={!canToggle} checked={done} onChange={() => onToggle(m)} />
          <span>{done ? "Completed" : "Mark complete"}</span>
        </label>
      </div>
    </div>
  );
}

function Mini({ label, value, accent = "text-ink-900", icon }: { label: string; value: number | string; accent?: string; icon?: React.ReactNode }) {
  return (
    <div className="card p-3">
      <div className="flex items-center justify-between text-ink-400">{icon}</div>
      <div className={`text-2xl font-semibold ${accent} mt-1`}>{value}</div>
      <div className="text-xs text-ink-500">{label}</div>
    </div>
  );
}

// Visually rich PDF/resource card matching the Figma design.
// Cover lookup: tries /resources/<cover>.jpg then .png; falls back to a
// branded gradient with the JSIR mark and the title overlaid.
function ResourceCard({ resource, large }: { resource: Resource; large?: boolean }) {
  const [coverFailed, setCoverFailed] = useState(false);
  const [coverExt, setCoverExt] = useState<"jpg" | "png">("jpg");
  const coverSrc = resource.cover ? `/resources/${resource.cover}.${coverExt}` : null;

  return (
    <div className="card overflow-hidden flex flex-col hover:shadow-md transition">
      <div className={`relative ${large ? "aspect-[3/2]" : "aspect-[3/2]"} overflow-hidden`}>
        {coverSrc && !coverFailed ? (
          <img
            src={coverSrc}
            alt={resource.title}
            className="w-full h-full object-cover"
            onError={() => {
              if (coverExt === "jpg") setCoverExt("png");
              else setCoverFailed(true);
            }}
          />
        ) : (
          <CoverFallback title={resource.title} large={!!large} />
        )}
        <div className="absolute top-3 right-3 bg-white rounded-full px-2.5 py-1 text-[10px] font-extrabold italic text-brand leading-none shadow-md">
          JESUS<br />SAID<br />IN RED<span className="text-brand">"</span>
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className={`font-semibold ${large ? "text-base" : "text-base"} text-center`}>{resource.title}</div>
        {resource.subtitle && <div className="text-xs text-ink-500 text-center mt-1">{resource.subtitle}</div>}
        <div className="mt-4 flex justify-center">
          <a
            href={resource.href}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
          >
            Download <Download className="h-4 w-4" />
          </a>
        </div>
      </div>
    </div>
  );
}

function CoverFallback({ title, large }: { title: string; large: boolean }) {
  // Brand-flavoured gradient placeholder until a real cover image is uploaded.
  return (
    <div className="w-full h-full bg-gradient-to-br from-ink-700 via-ink-900 to-brand-700 grid place-items-center p-6">
      <div className={`text-white font-extrabold ${large ? "text-3xl" : "text-2xl"} leading-tight tracking-tight text-center drop-shadow-sm`}>
        {title}
      </div>
    </div>
  );
}
